import { ApiError } from '../../api/client';
import type { PlanTier } from '../../api/types';

/**
 * FR-BIL-01 plan limits as the API reports them: `402 PLAN_LIMIT_REACHED` with
 * `details[0] = { issue: "plan_limit_<workspaces|projects|assets>", params: { limit, used, plan } }`
 * (totals of the billing owner's account, FR-BIL-16), and
 * `402 PLAN_FEATURE_UNAVAILABLE` with `{ issue: "plan_feature_<feature>", params: { plan } }`.
 */
export type PlanLimitInfo =
  | {
      kind: 'limit';
      resource: 'workspaces' | 'projects' | 'assets';
      limit: number;
      used: number;
      tier: PlanTier;
    }
  | { kind: 'feature'; feature: string; tier: PlanTier };

export const PLAN_ERROR_CODES = new Set(['PLAN_LIMIT_REACHED', 'PLAN_FEATURE_UNAVAILABLE']);

/** Tier of an item code (`smoke-free`, `smoke-pro-monthly`, `smoke-agency-monthly`). */
export function tierOfCode(code: string | number | undefined | null): PlanTier {
  const c = String(code ?? '').toLowerCase();
  if (c.includes('agency')) return 'agency';
  if (c.includes('pro')) return 'pro';
  return 'free';
}

const num = (v: string | number | undefined): number | null => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

export function planLimitInfo(error: unknown): PlanLimitInfo | null {
  if (!(error instanceof ApiError) || !PLAN_ERROR_CODES.has(error.code)) return null;
  const detail = error.details[0];
  const params = detail?.params ?? {};
  const tier = tierOfCode(params.plan);
  if (error.code === 'PLAN_LIMIT_REACHED') {
    const resource =
      detail?.issue === 'plan_limit_assets'
        ? 'assets'
        : detail?.issue === 'plan_limit_workspaces'
          ? 'workspaces'
          : 'projects';
    const limit = num(params.limit);
    const used = num(params.used);
    if (limit === null || used === null) return null;
    return { kind: 'limit', resource, limit, used, tier };
  }
  const feature = detail?.issue.replace(/^plan_feature_/, '') ?? 'unknown';
  return { kind: 'feature', feature, tier };
}
